import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

/**
 * SignatureCell — lazy-loads the decrypted signature on first open.
 * The records list endpoint strips `signature_encrypted` for performance and only
 * sends `has_signature` as a boolean. This component fetches the actual image
 * from /api/admin/records/{id}/signature on click.
 *
 * Props:
 *   - record:   { id, employee_name, has_signature?: bool, signature?: string }
 *   - API:      base API url
 *   - variant:  "icon" (default, just Eye icon) or "thumb" (inline preview once loaded)
 *   - requireCheckedOut: when true, only shows the button if record.is_checked_out
 */
export function SignatureCell({ record, API, variant = "icon", requireCheckedOut = false }) {
  const [sig, setSig] = useState(record?.signature || "");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const hasSig = record?.has_signature || !!record?.signature;
  if (!hasSig) return <span className="text-vault-text-secondary" data-testid={`no-signature-${record?.id}`}>—</span>;
  if (requireCheckedOut && !record?.is_checked_out) return <span className="text-vault-text-secondary">—</span>;

  const loadSignature = async () => {
    if (sig || loading) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/records/${record.id}/signature`);
      setSig(res.data?.signature || "");
      if (!res.data?.signature) toast.error("Signature not found");
    } catch (e) {
      toast.error("Failed to load signature");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) loadSignature(); }}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="text-vault-text-secondary hover:text-vault-gold"
          data-testid={`view-signature-${record.id}`}
          title="View signature"
        >
          {variant === "thumb" && sig ? (
            <img src={sig} alt="Signature" className="w-12 h-8 object-contain bg-black/30 rounded border border-vault-border" />
          ) : (
            <Eye className="w-4 h-4" />
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-vault-surface border-vault-border">
        <DialogHeader>
          <DialogTitle className="font-outfit text-vault-text">
            Signature - {record.employee_name}
          </DialogTitle>
        </DialogHeader>
        <div className="bg-black/50 rounded-lg p-4 min-h-40 flex items-center justify-center">
          {loading ? (
            <span className="text-vault-text-secondary text-sm" data-testid={`signature-loading-${record.id}`}>Loading signature…</span>
          ) : sig ? (
            <img src={sig} alt="Signature" className="w-full h-40 object-contain" data-testid={`signature-image-${record.id}`} />
          ) : (
            <span className="text-vault-text-secondary text-sm" data-testid={`signature-empty-${record.id}`}>No signature available.</span>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
